import type { HttpCore } from '../http';
import type {
  AdminAlert,
  AdminAlertsQuery,
  AdminAuditQuery,
  AdminAuthResponse,
  AdminDevice,
  AdminDeviceDetail,
  AdminDevicesQuery,
  AdminEnqueued,
  AdminImpersonation,
  AdminJobs,
  AdminMonthlyReport,
  AdminOverview,
  AdminPayment,
  AdminPaymentsQuery,
  AdminReportResend,
  AdminSubscription,
  AdminSubscriptionsQuery,
  AdminTenant,
  AdminUser,
  AdminUserDetail,
  AdminUserListItem,
  AdminUserTimeline,
  AdminUsersQuery,
  AdminCustomer,
  AdminsQuery,
  AuditEntry,
  AuthTokens,
  CreateAdminRequest,
  CreateTenantRequest,
  LoginRequest,
  OkResponse,
  Paged,
  PageQuery,
  TenantScopedQuery,
  UpdateTenantRequest,
} from '../types';

/**
 * `/api/v1/admin/**` — a completely separate identity (own collection, own JWT secret and audience).
 * Only ever call these through the *admin* client (`createAdminClient`), which stores its tokens
 * under `sf.admin.tokens` and refreshes against `/admin/auth/refresh`.
 *
 * Typed against the controllers in `sf.api/Controllers/Admin/**` — every list is
 * `{items, page, pageSize, total}` and every list accepts `?tenant=` (tenant id *or* slug); a
 * tenantadmin token is additionally pinned to its own tenant server-side.
 */
export function createAdminApi(http: HttpCore) {
  const q = (query: Record<string, string | number | boolean | undefined>) => ({ query });

  return {
    auth: {
      async login(body: LoginRequest): Promise<AdminAuthResponse> {
        const res = await http.post<AdminAuthResponse>('/admin/auth/login', body, { auth: false });
        http.setTokens(res);
        return res;
      },
      async refresh(): Promise<AuthTokens | null> {
        const refreshToken = http.getTokens()?.refreshToken;
        if (!refreshToken) return null;
        const res = await http.post<AuthTokens>(
          '/admin/auth/refresh',
          { refreshToken },
          { auth: false, skipRefresh: true },
        );
        http.setTokens(res);
        return res;
      },
      /** Revokes the refresh token server-side, then drops the local tokens either way. */
      async logout(): Promise<void> {
        const refreshToken = http.getTokens()?.refreshToken;
        try {
          if (refreshToken) await http.post<OkResponse>('/admin/auth/logout', { refreshToken });
        } catch {
          // signing out must never fail in the UI
        } finally {
          http.setTokens(null);
        }
      },
    },

    /** The signed-in admin (no `status` field — see AdminAuthController.Me). */
    me: () => http.get<AdminUser>('/admin/me'),

    overview: (query: TenantScopedQuery = {}) =>
      http.get<AdminOverview>('/admin/overview', q({ tenant: query.tenant })),

    /** Do the AI assistants recommend us? Latest run per engine. */
    visibility: () =>
      http.get<{
        summary: { engine: string; prompts: number; mentioned: number; lastCheckedAt: string }[];
        latest: { engine: string; lang: string; prompt: string; mentioned: boolean; snippet: string; error?: string; checkedAt: string }[];
        enginesConfigured: string[];
      }>('/admin/visibility'),

    tenants: {
      list: (query: PageQuery = {}) =>
        http.get<Paged<AdminTenant>>('/admin/tenants', q({ page: query.page, pageSize: query.pageSize })),
      get: (id: string) => http.get<AdminTenant>(`/admin/tenants/${encodeURIComponent(id)}`),
      /** Superadmin only (403 `forbidden` for a tenantadmin). */
      create: (body: CreateTenantRequest) => http.post<AdminTenant>('/admin/tenants', body),
      /** Superadmin only. Only the properties present in `body` are applied. */
      update: (id: string, body: UpdateTenantRequest) =>
        http.patch<AdminTenant>(`/admin/tenants/${encodeURIComponent(id)}`, body),
    },

    users: {
      list: (query: AdminUsersQuery = {}) =>
        http.get<Paged<AdminUserListItem>>(
          '/admin/users',
          q({
            q: query.q,
            tenant: query.tenant,
            status: query.status,
            includeDeleted: query.includeDeleted,
            page: query.page,
            pageSize: query.pageSize,
          }),
        ),
      get: (id: string) => http.get<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}`),
      /** Blocks the account and revokes every customer refresh token; returns the updated user. */
      block: (id: string) => http.post<AdminCustomer>(`/admin/users/${encodeURIComponent(id)}/block`),
      unblock: (id: string) => http.post<AdminCustomer>(`/admin/users/${encodeURIComponent(id)}/unblock`),
      /**
       * Mints a short-lived customer session so support can see the portal as this customer sees it.
       * Writes an audit entry server-side; nothing about the customer's own sessions changes.
       */
      impersonate: (id: string) =>
        http.post<AdminImpersonation>(`/admin/users/${encodeURIComponent(id)}/impersonate`),
      /** The ordered onboarding milestones for this customer, oldest first. */
      timeline: (id: string) => http.get<AdminUserTimeline>(`/admin/users/${encodeURIComponent(id)}/timeline`),
    },

    devices: {
      list: (query: AdminDevicesQuery = {}) =>
        http.get<Paged<AdminDevice>>(
          '/admin/devices',
          q({
            status: query.status,
            provider: query.provider,
            q: query.q,
            tenant: query.tenant,
            page: query.page,
            pageSize: query.pageSize,
          }),
        ),
      get: (id: string) => http.get<AdminDeviceDetail>(`/admin/devices/${encodeURIComponent(id)}`),
      /** Enqueues a `poll` work item (202). The worker picks it up within ~5 s. */
      pollNow: (id: string) => http.post<AdminEnqueued>(`/admin/devices/${encodeURIComponent(id)}/poll-now`),
      /** Enqueues a `revalidate` work item (202) — re-checks the stored credentials. */
      revalidate: (id: string) =>
        http.post<AdminEnqueued>(`/admin/devices/${encodeURIComponent(id)}/revalidate`),
      /** The months this device has a monthly report for, newest first. */
      reports: (id: string) =>
        http
          .get<{ items: AdminMonthlyReport[] }>(`/admin/devices/${encodeURIComponent(id)}/reports`)
          .then((r) => r.items ?? []),
      /** The customer's own report PDF, re-rendered from its snapshot. Audited on the server. */
      reportPdf: (id: string, month: string) =>
        http.blob(`/admin/devices/${encodeURIComponent(id)}/reports/${encodeURIComponent(month)}/pdf`),
      /** Sends that month's e-mail again, PDF attached (202). Never writes a second report row. */
      resendReport: (id: string, month: string) =>
        http.post<AdminReportResend>(
          `/admin/devices/${encodeURIComponent(id)}/reports/${encodeURIComponent(month)}/resend`,
        ),
    },

    alerts: {
      list: (query: AdminAlertsQuery = {}) =>
        http.get<Paged<AdminAlert>>(
          '/admin/alerts',
          q({
            status: query.status,
            type: query.type,
            tenant: query.tenant,
            page: query.page,
            pageSize: query.pageSize,
          }),
        ),
    },

    subscriptions: {
      list: (query: AdminSubscriptionsQuery = {}) =>
        http.get<Paged<AdminSubscription>>(
          '/admin/subscriptions',
          q({
            status: query.status,
            tenant: query.tenant,
            page: query.page,
            pageSize: query.pageSize,
          }),
        ),
    },

    payments: {
      list: (query: AdminPaymentsQuery = {}) =>
        http.get<Paged<AdminPayment>>(
          '/admin/payments',
          q({
            status: query.status,
            tenant: query.tenant,
            page: query.page,
            pageSize: query.pageSize,
          }),
        ),
    },

    /** Work queue + poll runs, newest 100 of each. Not paged. */
    jobs: (query: TenantScopedQuery = {}) => http.get<AdminJobs>('/admin/jobs', q({ tenant: query.tenant })),

    audit: {
      list: (query: AdminAuditQuery = {}) =>
        http.get<Paged<AuditEntry>>(
          '/admin/audit',
          q({
            action: query.action,
            tenant: query.tenant,
            page: query.page,
            pageSize: query.pageSize,
          }),
        ),
    },

    /** Superadmin only — a tenantadmin gets 403 on both calls. */
    admins: {
      list: (query: AdminsQuery = {}) =>
        http.get<Paged<AdminUser>>(
          '/admin/admins',
          q({
            q: query.q,
            role: query.role,
            tenant: query.tenant,
            page: query.page,
            pageSize: query.pageSize,
          }),
        ),
      create: (body: CreateAdminRequest) => http.post<AdminUser>('/admin/admins', body),
    },
  };
}

export type AdminApi = ReturnType<typeof createAdminApi>;
